import Tabs from "components/BaseWidgets/tab";
import {
  EngageFavorite,
  EngageImageRequest,
  EngageInterested,
  EngageProfileVisited,
  FilterIcon,
} from "assets/icons";
import { useContext, useEffect, useState } from "react";
import { ExploreContext } from "context/ExploreContext";
import Button from "components/BaseWidgets/button";
import Filter from "components/Explore/components/Filter";
import { EngageContactRequest } from "../../assets/icons";
import EngageLayout from "./components/EngageLayout/index";
import { useLocation } from "react-router-dom";
import "./style.scss";
import {
  useContactRequest,
  useFavorites,
  useImageRequest,
  useProfileInterested,
  useProfileVisited,
} from "hooks/api/useEngage";
import usePaginationWithProfileType from "hooks/usePaginationWithProfileType";

function Engage() {
  const location = useLocation();
  const tabIdFromState = location.state?.tabId || "1";
  const { clearAll, setLoading, filterData } = useContext(ExploreContext);
  const [tabActive, setTabActive] = useState(tabIdFromState);
  const [showFilter, setShowFilter] = useState(false);

  const visited = usePaginationWithProfileType();
  const interested = usePaginationWithProfileType();
  const contact = usePaginationWithProfileType();
  const imagerequested = usePaginationWithProfileType();
  const favorite = usePaginationWithProfileType();

  // Restore tab from sessionStorage if returning from profile
  useEffect(() => {
    const isFromProfileDetail = location.state?.fromProfileDetail === true;
    const savedTab = sessionStorage.getItem("engageActiveTab");

    if (isFromProfileDetail && savedTab) {
      setTabActive(savedTab);
    }
  }, []);

  const { data: visitedData, isLoading: isVisitedLoading } = useProfileVisited(
    {
      page: visited.page,
      limit: 8,
      profile_type: visited.profileType,
      ...(tabActive === "1" ? { filter_data: JSON.stringify(filterData) } : {}),
    },
    {
      enabled: tabActive === "1",
    },
  );

  const { data: interestedData, isLoading: isInterestedLoading } =
    useProfileInterested(
      {
        page: interested.page,
        limit: 8,
        profile_type: interested.profileType,
        ...(tabActive === "2"
          ? { filter_data: JSON.stringify(filterData) }
          : {}),
      },
      {
        enabled: tabActive === "2",
      },
    );

  useEffect(() => {
    const newTabId = location.state?.tabId;
    if (!newTabId) return; // if no tabId in state, do nothing
    if (newTabId !== tabActive) {
      setTabActive(newTabId);
      clearAll();
      paginations[newTabId]?.setPage(1);
    }
  }, [location.state?.tabId]);

  const { data: favoritesData, isLoading: isFavoritesLoading } = useFavorites(
    {
      page: favorite.page,
      limit: 8,
      profile_type: favorite.profileType,
      ...(tabActive === "3" ? { filter_data: JSON.stringify(filterData) } : {}),
    },
    {
      enabled: tabActive === "3",
    },
  );

  const { data: imageRequestData, isLoading: isImageRequestLoading } =
    useImageRequest(
      {
        page: imagerequested.page,
        limit: 8,
        profile_type: imagerequested.profileType,
        ...(tabActive === "4"
          ? { filter_data: JSON.stringify(filterData) }
          : {}),
      },
      {
        enabled: tabActive === "4",
      },
    );

  const { data: contactRequestData, isLoading: isContactRequestLoading } =
    useContactRequest(
      {
        page: contact.page,
        limit: 8,
        profile_type: contact.profileType,
        ...(tabActive === "5"
          ? { filter_data: JSON.stringify(filterData) }
          : {}),
      },
      {
        enabled: tabActive === "5",
      },
    );

  useEffect(() => {
    setLoading(
      isVisitedLoading ||
        isInterestedLoading ||
        isContactRequestLoading ||
        isFavoritesLoading ||
        isImageRequestLoading,
    );
  }, [
    isVisitedLoading,
    isInterestedLoading,
    isContactRequestLoading,
    isFavoritesLoading,
    isImageRequestLoading,
  ]);

  const paginations = {
    1: visited,
    2: interested,
    3: favorite,
    4: imagerequested,
    5: contact,
  };

  const parentTabItems = [
    {
      key: "1",
      label: (
        <span className="flex items-center gap-1-5">
          <EngageProfileVisited />
          Profiles visited
        </span>
      ),
      children: (
        <EngageLayout
          key={tabActive}
          tabVariant="1"
          data={visitedData?.users_data || []}
          count={visitedData?.pagination_details}
          current={visited.page}
          setCurrent={visited.setPage}
          setProfileType={visited.setProfileType}
          setVisitedPage={visited.setPage}
        />
      ),
    },
    {
      key: "2",
      label: (
        <span className="flex items-center gap-1-5">
          <EngageInterested />
          Interested
        </span>
      ),
      children: (
        <EngageLayout
          key={tabActive}
          tabVariant="2"
          tabActive={tabActive}
          data={interestedData?.users_data || []}
          count={interestedData?.pagination_details}
          current={interested.page}
          setCurrent={interested.setPage}
          setProfileType={interested.setProfileType}
          setVisitedPage={interested.setPage}
        />
      ),
    },
    {
      key: "3",
      label: (
        <span className="flex items-center gap-1-5">
          <EngageFavorite />
          Favorites
        </span>
      ),
      children: (
        <EngageLayout
          key={tabActive}
          tabVariant="3"
          tabActive={tabActive}
          data={favoritesData?.users_data || []}
          count={favoritesData?.pagination_details}
          current={favorite.page}
          setCurrent={favorite.setPage}
          setProfileType={favorite.setProfileType}
          setVisitedPage={favorite.setPage}
        />
      ),
    },
    {
      key: "4",
      label: (
        <span className="flex items-center gap-1-5">
          <EngageImageRequest />
          Image requests
        </span>
      ),
      children: (
        <EngageLayout
          key={tabActive}
          tabVariant="4"
          tabActive={tabActive}
          data={imageRequestData?.users_data || []}
          count={imageRequestData?.pagination_details}
          current={imagerequested.page}
          setCurrent={imagerequested.setPage}
          setProfileType={imagerequested.setProfileType}
          setVisitedPage={imagerequested.setPage}
        />
      ),
    },
    {
      key: "5",
      label: (
        <span className="flex items-center gap-1-5">
          <EngageContactRequest />
          View contact
        </span>
      ),
      children: (
        <EngageLayout
          tabVariant="5"
          tabActive={tabActive}
          data={contactRequestData?.users_data || []}
          count={contactRequestData?.pagination_details}
          current={contact.page}
          setCurrent={contact.setPage}
          setProfileType={contact.setProfileType}
          setVisitedPage={contact.setPage}
        />
      ),
    },
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [tabActive]);

  return (
    <div className="container pb-8 pt-12 explore-page">
      <Tabs
        activeKey={tabActive}
        onChange={(key) => {
          setTabActive(key);
          clearAll();
          paginations[key]?.setPage(1);
        }}
        variant="primary"
        position="left"
        items={parentTabItems}
        extraContent={{
          left: (
            <Button
              variant="custom"
              className="filter-icon"
              onClick={() => setShowFilter((prev) => !prev)}
            >
              <FilterIcon />
            </Button>
          ),
          right: <Filter show={showFilter} setShowFilter={setShowFilter} />,
        }}
      />
    </div>
  );
}

export default Engage;
